import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import SiteHeader from '../../components/SiteHeader';
import { formatPrice } from '../../data/modules';
import { useModuleCatalog } from '../../data/useModuleCatalog';

const SIGNUP_URL = 'https://panel.cafeotomasyonu.com/sign-up';

/**
 * Modül tanıtım sayfası — içeriğin tamamı `src/data/modules.js`ten geliyor.
 *
 * Modül başına elle yazılmış sayfa tutulduğu sürece özellik listeleri
 * ayrışıyordu: ana sayfadaki sekme güncelleniyor, modülün kendi sayfası eski
 * kalıyordu. Tek kaynak olduğu için artık ayrışamıyor.
 *
 * SEO başlığı ve açıklaması modül verisinden TÜRETİLİYOR; ayrı bir metin
 * alanı tutulsaydı o da ayrışan ikinci bir kopya olurdu.
 */
const ModuleDetailPage = ({ moduleId }) => {
  const { modules, purchasableModules } = useModuleCatalog();
  const module = modules.find((item) => item.id === moduleId);

  useEffect(() => {
    if (!module) return undefined;

    const title = `${module.name} — ${module.subtitle} | Cafe Otomasyonu`;
    const description = `${module.description} ${module.ideal} için.`;

    document.title = title;

    const meta =
      document.querySelector('meta[name="description"]') ||
      (() => {
        const created = document.createElement('meta');
        created.setAttribute('name', 'description');
        document.head.appendChild(created);
        return created;
      })();
    meta.setAttribute('content', description);

    const structured = {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: `${module.name} — Cafe Otomasyonu`,
      description,
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web, Windows, iOS, Android',
      featureList: module.features,
      offers: {
        '@type': 'Offer',
        price: String(module.price.monthly),
        priceCurrency: 'TRY',
        description:
          module.price.monthly === 0
            ? 'Bu modül süresiz ücretsizdir.'
            : `${module.trialDays} gün ücretsiz deneme, aylık abonelik.`
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structured);
    document.head.appendChild(script);

    return () => {
      if (script.parentNode) {
        document.head.removeChild(script);
      }
    };
  }, [module]);

  if (!module) {
    return (
      <main className="bg-white py-24">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h1 className="font-poppins font-heading-bold text-2xl text-foreground mb-4">
            Modül bulunamadı
          </h1>
          <Link to="/moduller" className="text-primary font-inter font-accent-semibold">
            Tüm modüllere dön
          </Link>
        </div>
      </main>
    );
  }

  const isFree = module.price.monthly === 0;
  const otherModules = purchasableModules.filter((item) => item.id !== module.id).slice(0, 4);

  return (
    <>
    <SiteHeader />
    <main className="bg-white">
      {/* Hero */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-4">
          <Link
            to="/moduller"
            className="inline-flex items-center space-x-2 text-sm text-text-secondary hover:text-primary mb-8"
          >
            <Icon name="ArrowLeft" size={16} />
            <span className="font-inter font-accent-medium">Tüm modüller</span>
          </Link>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <div className="flex items-center space-x-4 mb-6">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${module.color}`}>
                  <Icon name={module.icon} size={28} className="text-white" />
                </div>
                {module.badge && (
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                    module.badge === 'ÜCRETSİZ' ? 'bg-green-500 text-white' : 'bg-orange-500 text-white'
                  }`}>
                    {module.badge}
                  </span>
                )}
              </div>

              <h1 className="font-poppins font-heading-bold text-3xl md:text-5xl text-foreground mb-4">
                {module.name}
              </h1>
              <p className="font-poppins font-heading-semibold text-lg text-secondary mb-4">
                {module.subtitle}
              </p>
              <p className="font-open-sans text-lg text-text-secondary mb-8 leading-relaxed">
                {module.description}
              </p>

              <div className="flex flex-wrap gap-3">
                <a
                  href={SIGNUP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-3 rounded-lg bg-primary text-white font-poppins font-heading-semibold"
                >
                  {isFree ? 'Ücretsiz Kayıt Ol' : `${module.trialDays} Gün Ücretsiz Dene`}
                </a>
                {module.demoLink && (
                  <a
                    href={module.demoLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-6 py-3 rounded-lg border border-primary text-primary font-poppins font-heading-semibold"
                  >
                    {module.ctaText}
                  </a>
                )}
                {module.learnMore && (
                  <Link
                    to={module.learnMore.href}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-gray-900 text-white font-poppins font-heading-semibold"
                  >
                    <Icon name="Box" size={18} />
                    {module.learnMore.label}
                  </Link>
                )}
                {/* Kasa uygulaması POS modülünün parçası: indirme yeri burada
                    görünmezse müşteri onu ancak destekten öğreniyor. */}
                {moduleId === 'pos-system' && (
                  <Link
                    to="/download"
                    className="px-6 py-3 rounded-lg border border-gray-300 text-foreground font-poppins font-heading-semibold"
                  >
                    Kasa Uygulamasını İndir
                  </Link>
                )}
              </div>
            </div>

            {/* Fiyat kartı */}
            <div className="p-8 rounded-2xl border border-gray-200 bg-white shadow-lg">
              <p className="font-open-sans text-xs uppercase tracking-wide text-text-secondary mb-2">
                Aylık ücret
              </p>
              <div className="flex items-baseline space-x-2 mb-1">
                <span className="font-poppins font-heading-bold text-4xl text-foreground">
                  {formatPrice(module.price.monthly)}
                </span>
                {!isFree && (
                  <span className="font-open-sans text-sm text-text-secondary">/ay</span>
                )}
              </div>
              {!isFree && (
                <p className="font-open-sans text-sm text-text-secondary mb-6">
                  Yıllık ödemede {formatPrice(module.price.yearly)} — 2 ay hediye.
                  Fiyatlara KDV {module.vatIncluded ? 'dahildir' : 'dahil değildir'}.
                </p>
              )}
              {isFree && (
                <p className="font-open-sans text-sm text-green-600 mb-6">
                  Süresiz ücretsiz. Kredi kartı istemiyoruz.
                </p>
              )}

              {module.highlights?.length > 0 && (
                <div className="grid grid-cols-3 gap-3 mb-6">
                  {module.highlights.map((highlight) => (
                    <div key={highlight.label} className="text-center p-3 bg-gray-50 rounded-lg">
                      <p className="font-poppins font-heading-bold text-base text-primary">
                        {highlight.value}
                      </p>
                      <p className="font-open-sans text-[11px] text-text-secondary leading-tight">
                        {highlight.label}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              <div className="pt-6 border-t border-gray-100">
                <p className="font-open-sans text-xs uppercase tracking-wide text-text-secondary mb-2">
                  Kimin için
                </p>
                <p className="font-inter font-accent-medium text-sm text-foreground">
                  {module.ideal}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Özellikler */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="font-poppins font-heading-bold text-2xl md:text-3xl text-foreground mb-8">
            {module.name} neler yapıyor?
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {module.features.map((feature, index) => (
              <div
                key={index}
                className="flex items-start space-x-3 p-5 rounded-xl border border-gray-200 bg-white"
              >
                <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Icon name="Check" size={13} className="text-primary" />
                </div>
                <span className="font-open-sans text-sm text-foreground leading-relaxed">
                  {feature}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Diğer modüller */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="font-poppins font-heading-bold text-2xl text-foreground mb-8">
            Diğer modüller
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {otherModules.map((item) => (
              <Link
                key={item.id}
                to={item.path}
                className="p-5 rounded-xl bg-white border border-gray-200 hover:border-primary/40 hover:shadow-md transition-all duration-300"
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${item.color} mb-3`}>
                  <Icon name={item.icon} size={20} className="text-white" />
                </div>
                <p className="font-poppins font-heading-semibold text-sm text-foreground mb-1">
                  {item.name}
                </p>
                <p className="font-open-sans text-xs text-text-secondary">
                  {formatPrice(item.price.monthly)}
                  {item.price.monthly > 0 && ' / ay'}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
    </>
  );
};

export default ModuleDetailPage;
